import { useTheme } from "@emotion/react";
import { Columns, Typography } from "@fullscript/aviary-web";
import { faUser, faStethoscope } from "@fullscript/pro-solid-svg-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, Button, CircularIcon } from "@aviary";
import RupaFullscriptLogoDark from "@shared/assets/logo/RupaFullscriptLogoDark";
import RupaFullscriptLogoWhite from "@shared/assets/logo/RupaFullscriptLogoWhite";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useIsFullscriptOnly } from "@shared/hooks/useIsFullscriptOnly/useIsFullscriptOnly";
import { useIsRupaRedirectExperience } from "@shared/hooks/useIsRupaRedirectExperience/useIsRupaRedirectExperience";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "@shared/react-router-dom/react-router-dom";
import { l } from "@unauthenticated/shared/locales/i18n";

import { RoleTile } from "./RoleTile";

import * as styles from "./SignUpTriage.styles";

const SignUpTriage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tablet } = useBreakpoints();
  const [searchParams, setSearchParams] = useSearchParams();
  const isFullscriptOnly = useIsFullscriptOnly();
  const isRupaRedirectExperience = useIsRupaRedirectExperience();
  const [isPractitioner, setIsPractitioner] = useState(true);
  const { search } = useLocation();
  const { isCanada } = useSharedGlobalConfig();
  const currentTheme = useTheme();
  const isDarkMode = currentTheme.theme === "dark";

  const pracSignUp = () => {
    searchParams.set("step", "1");
    setSearchParams(searchParams);
  };

  const patientSignUp = () => {
    navigate(`/patient-signup${search}`);
  };

  const setRoute = () => {
    if (isPractitioner) {
      pracSignUp();
    } else {
      patientSignUp();
    }
  };

  const renderHeader = () => {
    if (isRupaRedirectExperience) {
      const Logo = isDarkMode ? RupaFullscriptLogoWhite : RupaFullscriptLogoDark;

      return (
        <>
          <Logo
            css={styles.rupaRedirectLogo}
            aria-label={t(l.common.RupaFullscriptLogoAlt)}
            role="img"
          />
          <Typography variant="h3" isMarginless customCss={styles.rupaRedirectHeading}>
            {t(l.common.RupaRedirectHeading)}
          </Typography>
          <Typography isMarginless customCss={styles.rupaRedirectSubheading}>
            {t(l.common.RupaRedirectSubheading)}
          </Typography>
        </>
      );
    }

    if (isFullscriptOnly) {
      return (
        <>
          <Typography variant="h2">{t(l.common.GetStartedWithFs)}</Typography>
          <Typography isMarginless>{t(l.common.SelectYourAccountType)}</Typography>
        </>
      );
    }

    return (
      <>
        <Typography variant="h2">{t(l.common.GetStartedWithFs)}</Typography>
        <Typography isMarginless>{t(l.common.WhichTypeOfAccount)}</Typography>
      </>
    );
  };

  const handleRoleSelectionKeydown = (action: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      action();
    }
  };

  const renderPractitionerTileDescription = () => {
    if (isRupaRedirectExperience) {
      return t(l.common.RupaRecommendLabsAndSupplements);
    }

    if (isCanada) {
      return t(l.common.RecommendSupplements);
    }

    return t(l.common.RecommendSupplementsAndLabs);
  };

  const renderPatientTileDescription = () => {
    if (isRupaRedirectExperience) {
      return t(l.common.RupaOrderLabsAndSupplements);
    }

    if (isCanada) {
      return t(l.common.AccessrSupplements);
    }

    return t(l.common.OrderSupplementsAndLabs);
  };

  const renderTiles = () => {
    if (isFullscriptOnly) {
      return (
        <>
          <Columns.Column
            customCss={styles.boxTile}
            columnWidth={6}
            phoneWidth={12}
            onClick={() => pracSignUp()}
            tabIndex={0}
            role="button"
            onKeyDown={handleRoleSelectionKeydown(pracSignUp)}
          >
            <Arrange justify="center" alignment="center" isVertical>
              <CircularIcon size="large" icon={faStethoscope} />
              <Typography variant="h4">{t(l.common.PracAcccount)}</Typography>
              <Typography>{renderPractitionerTileDescription()}</Typography>
            </Arrange>
          </Columns.Column>
          <Columns.Column
            customCss={styles.boxTile}
            columnWidth={6}
            phoneWidth={12}
            onClick={() => patientSignUp()}
            tabIndex={0}
            role="button"
            onKeyDown={handleRoleSelectionKeydown(patientSignUp)}
          >
            <Arrange justify="center" alignment="center" isVertical>
              <CircularIcon size="large" icon={faUser} />
              <Typography variant="h4">{t(l.common.PatientAccount)}</Typography>
              <Typography>{renderPatientTileDescription()}</Typography>
            </Arrange>
          </Columns.Column>
        </>
      );
    }

    return (
      <>
        <RoleTile
          name={t(l.common.PracAcccount)}
          icon={faStethoscope}
          isSelected={isPractitioner}
          onSelected={() => setIsPractitioner(true)}
          isFullHeight={tablet.lessThan}
        />
        <RoleTile
          name={t(l.common.PatientAccount)}
          icon={faUser}
          isSelected={!isPractitioner}
          onSelected={() => setIsPractitioner(false)}
          isFullHeight={tablet.lessThan}
        />
      </>
    );
  };

  return (
    <div css={styles.contentBox}>
      {renderHeader()}
      <form onSubmit={setRoute}>
        <Columns customCss={styles.tilesContainter}>{renderTiles()}</Columns>
        {!isFullscriptOnly && (
          <Button
            type="submit"
            onClick={setRoute}
            isFullWidth={tablet.lessThan}
            data-e2e="role-selection-button"
          >
            {t(l.common.Next)}
          </Button>
        )}
      </form>
    </div>
  );
};

export { SignUpTriage };
